/**
 * Parses error messages from JSON strings returned by the API.
 * Extracts the actual error message from the JSON error response format.
 *
 * @param errorString - The error string from the API response
 * @returns The parsed error message, or the original string if parsing fails
 */
export const parseErrorMessage = (errorString: string): string => {
  try {
    // Extract JSON from the error message (handles both \n literal and actual newlines)
    const jsonMatch = errorString.match(/Message:\s*({[\s\S]*})/);
    if (jsonMatch) {
      const jsonStr = jsonMatch[1];
      const parsed = JSON.parse(jsonStr) as {
        // eslint-disable-next-line camelcase
        error?: { error_msg?: string; error_name?: string };
      };
      // eslint-disable-next-line camelcase
      return parsed.error?.error_msg ?? errorString;
    }
  } catch {
    // If parsing fails, return the original string
  }
  return errorString;
};

/**
 * Type for error key filter function
 */
type ErrorKeyFilter = (key: string) => boolean;

/**
 * Collects and parses group_error messages from an errors object.
 * Filters errors based on the provided key filter function and returns unique parsed messages.
 *
 * @param errors - The errors object from the API response
 * @param keyFilter - Function to filter which error keys to process
 * @returns Array of unique parsed error messages
 */
export const collectGroupErrorMessages = (
  errors: Record<string, Array<string>> | undefined,
  keyFilter: ErrorKeyFilter,
): string[] => {
  const groupErrorMessages: string[] = [];
  if (errors) {
    Object.keys(errors).forEach(key => {
      if (keyFilter(key)) {
        const errorArray = errors[key];
        if (Array.isArray(errorArray) && errorArray.length > 0) {
          errorArray.forEach(errorMsg => {
            const parsedMsg = parseErrorMessage(errorMsg);
            if (parsedMsg && !groupErrorMessages.includes(parsedMsg)) {
              groupErrorMessages.push(parsedMsg);
            }
          });
        }
      }
    });
  }
  return groupErrorMessages;
};
